import isEmpty from 'lodash/isEmpty';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { CatalogDownloadModal } from '@/components/CatalogDownloadModal';
import { Icon } from '@/components/Icon';
import type {
  ContentfulFooterFragment,
  ContentfulMainMenuItemFragment,
} from '@/graphql/contentful/api';
import { useContactInfo } from '@/hooks/useContactInfo';
import { getBrandConfig } from '@/lib/configuration/configuration';
import { CatalogDownloadDesc } from '@/messages/catalogDownload';
import { commonMessages } from '@/messages/common';
import { NewsLetterDesc } from '@/messages/newsletter';

import { Flex } from '../Flex';
import { Logo } from '../Logo';
import { NewsLetterModal } from '../NewsLetterModal';
import { Typography } from '../Typography';
import { ContactInfo, LinkItem, Links, Menu } from './components';

import * as S from './Footer.styles';

export type FooterColorVariant =
  | 'default'
  | 'defaultInverted'
  | 'primary'
  | 'primaryInverted';

interface FooterProps {
  disableListenEvent?: boolean;
  footerData: ContentfulFooterFragment;
}

export const Footer = ({ disableListenEvent, footerData }: FooterProps) => {
  const [isShowNewsLetterPopup, setIsShowNewsLetterPopup] = useState(false);
  const [isShowDownloadCatalogPopup, setIsShowDownloadCatalogPopup] =
    useState(false);
  const contactInfo = useContactInfo();

  const { formatMessage } = useIntl();
  const defaultCopyRight = formatMessage(commonMessages.defaultCopyRight);
  const year = new Date().getFullYear();
  const copyRightWithYear = defaultCopyRight.replaceAll(
    '{year}',
    year.toString(),
  );

  const toggleNewsLetterPopup = (e?: React.MouseEvent) => {
    e?.preventDefault();
    setIsShowNewsLetterPopup(!isShowNewsLetterPopup);
  };

  const toggleDownloadCatalogPopup = () => {
    setIsShowDownloadCatalogPopup(!isShowDownloadCatalogPopup);
  };

  const brandConfig = getBrandConfig();
  const isSimpleFooter = brandConfig.simpleFooter;

  const handleNewsletter = () => {
    setIsShowNewsLetterPopup(true);
  };

  useEffect(() => {
    if (disableListenEvent) return;

    window.addEventListener('newsletter', handleNewsletter);
    return () => {
      window.removeEventListener('newsletter', handleNewsletter);
    };
  }, [handleNewsletter, disableListenEvent]);
  return (
    <>
      <S.Wrapper data-test-id='FooterWrapper' forwardedAs='footer'>
        <S.FooterBox>
          {isEmpty(footerData) ? (
            <S.DefaultCopyRightWrapper style={{ color: 'white' }}>
              {copyRightWithYear}
            </S.DefaultCopyRightWrapper>
          ) : (
            <S.InnerWrapper pb={{ xs: 8, md: 4 }} pt={4}>
              {(footerData.displayNewsletterSubscribe ||
                footerData.displayContactInformation) && (
                <S.ContactInfoWrapper>
                  {footerData.displayNewsletterSubscribe && (
                    <S.Subscribe
                      data-test-id='FooterSubscribe'
                      onClick={toggleNewsLetterPopup}
                    >
                      <Flex>
                        <Typography
                          className='subscribeText'
                          data-test-id='FooterSubscribeText'
                        >
                          {footerData.subscribeToNewsletterText}
                        </Typography>
                        <Icon
                          data-test-id='FooterSubscribeIcon'
                          name='chevronRight'
                          size='m'
                          variant='regular'
                        />
                      </Flex>
                    </S.Subscribe>
                  )}
                  {footerData.displayContactInformation && (
                    <Footer.ContactInfo contactInfo={contactInfo} />
                  )}
                </S.ContactInfoWrapper>
              )}
              {!isSimpleFooter && (
                <Footer.Menu
                  footerData={footerData}
                  openDownloadCatalogPopup={toggleDownloadCatalogPopup}
                />
              )}
              {isSimpleFooter && (
                <S.LogoWrapper>
                  <Logo variant='logoLine' />
                </S.LogoWrapper>
              )}
              {footerData.logo?.url && (
                <S.LogoWrapper>
                  <Logo
                    disableClick={isEmpty(footerData.logoLink)}
                    heightDesktop={footerData.logoHeightDesktop}
                    heightMobile={footerData.logoHeightMobile}
                    link={footerData.logoLink}
                    src={footerData.logo.url}
                    widthDesktop={footerData.logoWidthDesktop}
                    widthMobile={footerData.logoWidthMobile}
                  />
                </S.LogoWrapper>
              )}
              <S.Break />
              <Footer.Links
                copyRight={footerData.copyrightText ?? ''}
                data-test-id='FooterLinks'
                links={
                  footerData.secondaryMenuItemsCollection
                    ?.items as ContentfulMainMenuItemFragment[]
                }
              />
            </S.InnerWrapper>
          )}
        </S.FooterBox>
        <S.Tail />
      </S.Wrapper>
      <NewsLetterModal
        isOpen={isShowNewsLetterPopup}
        onClose={() => setIsShowNewsLetterPopup(false)}
      >
        <S.NewsLetterDesc
          color='default'
          fontFamily='openSans'
          mb={2}
          mt={2}
          textAlign='center'
        >
          {NewsLetterDesc}
        </S.NewsLetterDesc>
      </NewsLetterModal>
      <CatalogDownloadModal
        isOpen={isShowDownloadCatalogPopup}
        onClose={() => setIsShowDownloadCatalogPopup(false)}
      >
        <Typography
          color='default'
          fontFamily='openSans'
          mb={2}
          mt={2}
          textAlign='center'
        >
          {CatalogDownloadDesc}
        </Typography>
      </CatalogDownloadModal>
    </>
  );
};

Footer.Links = Links;
Footer.Link = LinkItem;
Footer.ContactInfo = ContactInfo;
Footer.Menu = Menu;

Footer.displayName = 'Footer';
